import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import DataHandler from 'config/DataHandler';
import { storageKeys } from 'config/settings.json';
import StorageHandler from 'config/StorageHandler';

interface Lang {
  code: string | null;
  country: string | null;
  langFile: string | null;
}

interface Socials {
  tiktok: string | null;
  facebook: string | null;
  flickr: string | null;
  instagram: string | null;
  twitter: string | null;
  youtube: string | null;
  pinterest: string | null;
  googlePlus: string | null;
  linkedin: string | null;
  weibo: string | null;
}

export interface AppInfo {
  appName: string | null;
  backgroundImage: string | null;
  backgroundImages: string[];
  backgroundVideo: string | null;
  generalBackgroundImage: string | null;
  landingQuote: string | null;
  loginImage: string | null;
  logo: string | null;
  logoWhite: string | null;
  logoWide: string | null;
  logoWideWhite: string | null;
  primaryColor: string | null;
  profileImageHeader: string | null;
  programBenefits: string[];
  secondaryColor: string | null;
  socials: Socials;
  languages: string[];
  featureFlags: string[];
  trainLearnConfig: string | null;
}

export interface UseSettingsResult {
  loaded: boolean;
  appId: string | null;
  lang: Lang;
  appInfo: AppInfo;
  salesCompanionAppId?: string | number;
  email: string | null;
}

interface SettingsContextValue extends UseSettingsResult {
  fetchSettings: () => Promise<void>;
}

interface RawLangResponse {
  code?: string;
  country?: string;
  langFile?: string;
}

interface RawAppInfoResponse extends Partial<AppInfo> {
  salesCompanionAppId?: string | number;
}

interface RawUserObject {
  email?: string;
}

const defaultLang: Lang = {
  code: null,
  country: null,
  langFile: null,
};

const defaultSocials: Socials = {
  tiktok: null,
  facebook: null,
  flickr: null,
  instagram: null,
  twitter: null,
  youtube: null,
  pinterest: null,
  googlePlus: null,
  linkedin: null,
  weibo: null,
};

const defaultAppInfo: AppInfo = {
  appName: null,
  backgroundImage: null,
  backgroundImages: [],
  backgroundVideo: null,
  generalBackgroundImage: null,
  landingQuote: null,
  loginImage: null,
  logo: null,
  logoWhite: null,
  logoWide: null,
  logoWideWhite: null,
  primaryColor: null,
  profileImageHeader: null,
  programBenefits: [],
  secondaryColor: null,
  socials: defaultSocials,
  languages: [],
  featureFlags: [],
  trainLearnConfig: null,
};

const defaultSettingsState: UseSettingsResult = {
  loaded: false,
  appId: null,
  lang: defaultLang,
  appInfo: defaultAppInfo,
  email: null,
};

const settingsContext = createContext<SettingsContextValue | null>(null);

const parseLang = (langResponse: RawLangResponse): Lang => {
  const { code = defaultLang.code, country = defaultLang.country, langFile = defaultLang.langFile } = langResponse;

  return { code, country, langFile };
};

const parseAppInfo = (appInfoResponse: RawAppInfoResponse): AppInfo => {
  const {
    appName = defaultAppInfo.appName,
    backgroundImage = defaultAppInfo.backgroundImage,
    backgroundImages = defaultAppInfo.backgroundImages,
    backgroundVideo = defaultAppInfo.backgroundVideo,
    generalBackgroundImage = defaultAppInfo.generalBackgroundImage,
    landingQuote = defaultAppInfo.landingQuote,
    loginImage = defaultAppInfo.loginImage,
    logo = defaultAppInfo.logo,
    logoWhite = defaultAppInfo.logoWhite,
    logoWide = defaultAppInfo.logoWide,
    logoWideWhite = defaultAppInfo.logoWideWhite,
    primaryColor = defaultAppInfo.primaryColor,
    profileImageHeader = defaultAppInfo.profileImageHeader,
    programBenefits = defaultAppInfo.programBenefits,
    secondaryColor = defaultAppInfo.secondaryColor,
    socials = defaultAppInfo.socials,
    languages = defaultAppInfo.languages,
    featureFlags = defaultAppInfo.featureFlags,
    trainLearnConfig = defaultAppInfo.trainLearnConfig,
  } = appInfoResponse;

  return {
    appName,
    backgroundImage,
    backgroundImages,
    backgroundVideo,
    generalBackgroundImage,
    landingQuote,
    loginImage,
    logo,
    logoWhite,
    logoWide,
    logoWideWhite,
    primaryColor,
    profileImageHeader,
    programBenefits,
    secondaryColor,
    socials,
    languages,
    featureFlags,
    trainLearnConfig,
  };
};

interface SettingsProviderProps {
  children: React.ReactNode;
}

function SettingsProvider({ children }: SettingsProviderProps) {
  const [appSettings, setAppSettings] = useState<UseSettingsResult>(defaultSettingsState);

  const fetchSettings = useCallback(async () => {
    const [newAppId, newLang, newAppInfo, userObj] = await Promise.all([
      DataHandler.matchAppId() as Promise<string | undefined>,
      StorageHandler.load(storageKeys.lang) as Promise<RawLangResponse>,
      StorageHandler.load(storageKeys.appInfo) as Promise<RawAppInfoResponse>,
      StorageHandler.load(storageKeys.user) as Promise<RawUserObject | null>,
    ]);

    setAppSettings({
      loaded: true,
      appId: newAppId ?? null,
      lang: parseLang(newLang),
      appInfo: parseAppInfo(newAppInfo),
      salesCompanionAppId: newAppInfo.salesCompanionAppId,
      email: userObj?.email ?? null,
    });
  }, []);

  const settingsValues = useMemo<SettingsContextValue>(
    () => ({
      ...appSettings,
      fetchSettings,
    }),
    [appSettings, fetchSettings],
  );

  return <settingsContext.Provider value={settingsValues}>{children}</settingsContext.Provider>;
}

export function useSettings(): UseSettingsResult {
  const context = useContext(settingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }

  const { loaded, appId, lang, appInfo, fetchSettings, salesCompanionAppId, email } = context;

  useEffect(() => {
    if (!loaded) void fetchSettings();
  }, [fetchSettings, loaded]);

  return {
    loaded,
    appId,
    lang,
    appInfo,
    salesCompanionAppId,
    email,
  };
}

export { SettingsProvider };
